import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component, DoCheck, ElementRef,
  EventEmitter, Inject,
  Input,
  OnChanges, OnDestroy,
  OnInit,
  Output,
  SimpleChanges, ViewChild
} from "@angular/core";
import * as _ from "lodash";
import {CdkVirtualScrollViewport} from "@angular/cdk/scrolling";
import {fromEvent, Subscription, throttleTime} from "rxjs";
import {
  RoutesViewSettingsServicesModel
} from "../../../../routes/models/routes-view-settings.model";
import {ServicesComponentState, ServicesComponentStore} from "../../state/services.store";
import {RoutePanelModel} from "../../../../routes/models/route-panel.model";
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragEnter,
  CdkDropList,
  moveItemInArray,
  transferArrayItem
} from "@angular/cdk/drag-drop";
import {Service} from "../../../models/service.model";
import {TaskModel} from "../../../../tasks/models/task.model";
import {PanelService} from "../../../../commons/angular/services/panel.service";
import {
  TaskRelationsPopupComponent,
  TaskRelationsPopupData
} from "../../../../commons/angular/components/task-relations-popup/task-relations-popup.component";
import {TasksHttpService} from "../../../../tasks/angular/services/tasks-http.service";
import {BringozMenuPanelComponent} from "../../../../commons/angular/components/bringoz-menu-panel/bringoz-menu-panel.component";
import {RoutesDragAndDropService} from "../../../../routes/angular/services/routes-drag-and-drop.service";
import {DEFAULT_SERVICES_COLUMN_WIDTHS} from "../../../../routes/constants/routes-column-widths.constant";
import {SERVICES_COLUMN_GROUPS} from "../../../../routes/constants/routes-column-groups.constant";
import {dedupeRelationsByService, flattenTaskRelations} from "../../../../tasks/utils/task-relations.util";
import {TaskPurposeType} from "../../../../commons/types/task-purpose.type";
import {TaskRelationModel} from "../../../../tasks/models/task-relation.model";

require('./services-table-list.scss');
@Component({
  standalone: false,
  selector: 'app-services-table-list',
  templateUrl: './services-table-list.html',
  providers: [ServicesComponentStore],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ServicesTableListComponent implements OnInit, OnChanges, OnDestroy {

  @Input() panel: RoutePanelModel;
  @Input() panels: RoutePanelModel[];
  @Input() resourcesDropListIds: string[];
  @Input() routesDropListIds: string[];
  @Input() settings: RoutesViewSettingsServicesModel;
  @Input() from?: string;
  @Input() to?: string;
  @Input() queryString?: string;
  @Input() bringozFilters?: any;
  @Input() selectedIds: string[];
  @Input() itemElementSize: string;
  @Output() onSelectServices = new EventEmitter<string[]>();
  @Output() onSelect = new EventEmitter<any>();
  @Output() fetchNextEvent = new EventEmitter<null>();
  @Output() onToggleVisible = new EventEmitter<boolean>();
  @Output() onRemoveTaskFromRoute = new EventEmitter<any>();
  @Output() onSplitByPackages = new EventEmitter<{ item: Service | TaskModel; event: MouseEvent }>();
  @Output() onSettingsChange = new EventEmitter<RoutesViewSettingsServicesModel>();
  @Output() onDrillDown = new EventEmitter<{ routeId: string; event: any }>();

  get uniqueId(): string {
    return 'services-' + this.panel?.id;
  }

  public defaultWidths = DEFAULT_SERVICES_COLUMN_WIDTHS;
  public columnGroups = SERVICES_COLUMN_GROUPS;

  @ViewChild(CdkVirtualScrollViewport) private viewport: CdkVirtualScrollViewport;
  @ViewChild('headerContainer') private headerContainer: ElementRef;

  public vm: ServicesComponentState;
  public selectedKeys: any;
  public visibleKeys: any;
  public allPanelIds: string[];
  public enterPredicate: (drag: CdkDrag<any>, drop: CdkDropList<any>) => boolean;
  public relationsMap: {[serviceId: string]: TaskRelationModel[]} = {};

  private readonly loadLimit: number = 30;
  private vmSubscription: Subscription;
  private _contextMenuSubscription: Subscription;

  constructor(
    @Inject(ServicesComponentStore) private servicesStore: ServicesComponentStore,
    @Inject(PanelService) private panelService: PanelService,
    @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef,
    @Inject(TasksHttpService) private tasksHttpService: TasksHttpService,
    @Inject(RoutesDragAndDropService) private routesDragAndDropService: RoutesDragAndDropService) {
      this.enterPredicate = this.routesDragAndDropService.enterPredicateServices;
  }

  ngOnInit() {
    this.vmSubscription = this.servicesStore.vm$.subscribe((vm) => {
      this.vm = {...vm};
      this.relationsMap = {};
      (this.vm.services || []).forEach(service => {
        this.relationsMap[service.id] = dedupeRelationsByService(flattenTaskRelations(service.taskList), TaskPurposeType.Dropoff);
      });
      this.cdr.detectChanges();

      setTimeout(() => {
        if (this.viewport && !this.vm.loading && !!this.vm.cursor) {
          this.viewport.checkViewportSize();
          const nextBatchThreshold = this.viewport.getViewportSize();
          const bottomOffset = this.viewport.measureScrollOffset('bottom');

          if (bottomOffset < nextBatchThreshold) {
            this._getServices();
          }
        }
      }, 0);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.panels || changes.resourcesDropListIds || changes.routesDropListIds) {
      const drilldownLists = [];
      const filteredPanels = _.filter(this.panels, (p) => {
        if (p.id === this.panel.id) {
          return false;
        }
        if (p.selectedTabIndex === 0) {
          drilldownLists.push(`${p.id}-drilldown`);
        }
        return true;
      });
      this.allPanelIds = _.concat(_.map(filteredPanels, 'id'), this.resourcesDropListIds || [], this.routesDropListIds || [], drilldownLists);
    }
    if (changes.selectedIds) {
      this.selectedKeys = {};
      _.forEach(this.selectedIds, (id) => {
        this.selectedKeys[id] = true;
      });
    }
    if (
      changes.queryString  ||
      changes.from ||
      changes.to ||
      changes.bringozFilters
    ) {
      this._resetStore();
      this._getServices();
    }
  }

  ngOnDestroy() {
    this.vmSubscription?.unsubscribe();
    this._contextMenuSubscription?.unsubscribe();
  }

  onRightClick(event: MouseEvent, service: Service) {
    event.preventDefault();
    const panel = this.panelService.open(BringozMenuPanelComponent, event, [
      {
        name: 'Split Packages',
        action: 'splitByPackages',
        iconName: 'BOX',
        disabled: false // Note: could bind to a specific allowance check if needed
      }], {
      position: {
        originX: 'center',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'top',
      },
      width: 130
    });

    if (panel) {
      if (this._contextMenuSubscription) {
        this._contextMenuSubscription.unsubscribe();
      }

      this._contextMenuSubscription = panel.instance.onClose
        .subscribe((action?: string) => {
          switch (action) {
            case 'splitByPackages':
              this.onSplitByPackages.emit({ item: service, event: event });
              break;
            default:
              break;
          }

          this._contextMenuSubscription?.unsubscribe();
        });
    }
  }

  onBodyScroll(event: Event) {
    if (this.headerContainer) {
      const target = event.target as HTMLElement;
      this.headerContainer.nativeElement.scrollLeft = target.scrollLeft;
    }
    this.getNextBatch(event);
  }

  getNextBatch(event: Event) {
    const nextBatchThreshold = this.viewport.getViewportSize();
    if (
      !this.vm.loading &&
      this.viewport.measureScrollOffset('bottom') < nextBatchThreshold &&
      !!this.vm.cursor
    ) {
      this._getServices();
    }
    this.viewport.checkViewportSize();
  }

  onToggleSelection(id, isSelected: boolean) {
    this.selectedKeys[id] = isSelected;
    const selectedIds = _.map(this.selectedKeys, (value, key) => {
      if (value) return key;
    });
    this.onSelectServices.emit(_.compact(selectedIds));
  }

  onRouteIdClick(event: any, routeId: string) {
    event.stopPropagation();
    this.onDrillDown.emit({ routeId, event });
  }

  onRelationsClick(event: MouseEvent, service: Service) {
    const relations = this.relationsMap[service.id] || [];
    const taskIds = _.map(service.taskList, 'id');
    this.panelService.open<TaskRelationsPopupComponent>(TaskRelationsPopupComponent, event, {
      relations,
      tasks$: this.tasksHttpService.getTasks(taskIds)
    } as TaskRelationsPopupData);
  }

  drop(event: CdkDragDrop<any[]>) {
    const result = this.routesDragAndDropService.onDropServices(event, this.selectedIds);
    if (result) {
      this.onRemoveTaskFromRoute.emit(result);
    }
    return false;
  }

  private _resetStore() {
    this.servicesStore.resetServices();
  }

  private _getServices() {
    this.servicesStore.getServices(this._getServicesQuery());
  }

  private _getServicesQuery() {
    const query = {
      from: this.from,
      to: this.to,
      group: this.bringozFilters.serviceStatusGroup,
      serviceTypes: this.bringozFilters.serviceTypes,
      branchIds: this.bringozFilters.branchIds,
      agentBranchIds: this.bringozFilters.agentBranchIds,
      depotIds: this.bringozFilters.depotIds,
      accountIds: this.bringozFilters.accountIds,
      agentAccountIds: this.bringozFilters.supplyAccountIds,
      agentIds: this.bringozFilters.agentIds,
      teamIds: this.bringozFilters.teamIds,
      query: this.queryString,
      areaIds: this.bringozFilters.serviceAreaIds,
      beforeTimeWindow: this.bringozFilters.timeToDeliver === 'beforeTimeWindow' || undefined,
      afterTimeWindow: this.bringozFilters.timeToDeliver === 'afterTimeWindow' || undefined,
      noArea: this.bringozFilters.noArea,
      noPickUpArea: this.bringozFilters.noPickUpArea,
      noDropOffArea: this.bringozFilters.noDropOffArea,
      taskApprovalStatuses: this.bringozFilters.approvalStatuses,
      label: this.bringozFilters.labels,
      tagNames: this.bringozFilters.tags,
      vehicleTypeIds: this.bringozFilters.vehicleTypeIds,
      riskTypes: this.bringozFilters.riskTypes,
      programType: this.bringozFilters.programTypes,
      inRoute: this.bringozFilters.inRoute,
      isPacked: this.bringozFilters.isPacked,
      packingStatus: this.bringozFilters.packingStatuses,
      dispatchQuoteType: this.bringozFilters.dispatchQuoteTypes,
      missingPackageItems: this.bringozFilters.isMissingPackages,
      packageTypes: this.bringozFilters.packagesIds,
      configuredFieldId: this.bringozFilters.configuredFieldName,
      configuredFieldValue: this.bringozFilters.configuredFieldValue,
      reasonType: this.bringozFilters.reasonType,
      agentTypes: this.bringozFilters.agentType,
      limit: this.loadLimit,
    };
    Object.keys(query).forEach(key => query[key] === undefined && delete query[key]);
    return query;
  }

  public trackById(_ :number, item: Service): string {
    return item.id;
  }
}

